import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Plus, Trash2, GraduationCap, Briefcase, ShieldCheck,
  Settings2, MapPin, Clock, Calendar, ChevronDown, ChevronUp,
  Sparkles, Bus, Sun
} from 'lucide-react';
import { getActiveProfile } from '../../config/arizona-sped';

const profile = getActiveProfile();

const EMPTY_SERVICE = {
  id: '', category: '', subcategory: '', delivery_type: '', provider: '',
  setting: '', frequency_amount: '', frequency_unit: 'per week', minutes_per_session: '',
  start_date: '', end_date: '', description: '',
};

const EMPTY_AID = { id: '', name: '', description: '', setting: '', frequency: '' };
const EMPTY_MOD = { id: '', name: '', description: '' };

function mergeContent(existing) {
  const defaults = {
    special_education: [], related_services: [], supplementary_aids: [],
    program_modifications: [], placement: '', placement_percentage_gen_ed: '',
    placement_percentage_sped: '',
    extended_school_year: { eligible: null, justification: '' },
    transportation: { specialized: null, description: '' },
    lre_explanation: '', lre_removal_justification: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  return { ...defaults, ...existing };
}

/** Migrate old service_type field → category for backward compat */
function normalizeService(svc) {
  if (svc.category) return svc;
  if (svc.service_type && !svc.category) {
    return { ...svc, category: svc.service_type, subcategory: svc.subcategory || '' };
  }
  return svc;
}

function CollapsibleGroup({ title, icon: Icon, count, badge, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid={testId}>
      <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FDFCFB] transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2.5">
          <Icon className="h-4.5 w-4.5 text-[#3B6A9B]" />
          <span className="text-sm font-semibold text-[#1C1C1C]">{title}</span>
          {count > 0 && <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{count}</Badge>}
          {badge}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#E5E5E5]">{children}</div>}
    </div>
  );
}

/** Reusable cascading category → subcategory service row */
function ServiceRow({ service, idx, serviceGroup, categoryList, onUpdate, onRemove }) {
  const svc = normalizeService(service);
  const update = (field, value) => onUpdate(serviceGroup, idx, field, value);

  const selectedCat = categoryList.find(c => c.label === svc.category || c.id === svc.category);
  const hasSubcategories = selectedCat?.subcategories?.length > 0;

  const handleCategoryChange = (val) => {
    update('category', val);
    update('subcategory', '');
  };

  const groupLabel = serviceGroup === 'special_education' ? 'SDI' : 'Related';

  return (
    <div className="bg-[#FDFCFB] border border-[#E5E5E5] rounded-xl p-4 space-y-3" data-testid={`service-row-${serviceGroup}-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#595959]">{groupLabel} Service {idx + 1}</span>
        <button onClick={() => onRemove(serviceGroup, idx)} className="p-1 rounded-lg hover:bg-red-50" data-testid={`remove-service-${serviceGroup}-${idx}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-400" />
        </button>
      </div>

      {/* Row 1: Category + Subcategory */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Category</label>
          <Select value={svc.category || undefined} onValueChange={handleCategoryChange}>
            <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`svc-category-${serviceGroup}-${idx}`}>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categoryList.map(c => (
                <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasSubcategories ? (
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">
              Subcategory
              <span className="text-[10px] text-[#595959] font-normal ml-1">({selectedCat.label})</span>
            </label>
            <Select value={svc.subcategory || undefined} onValueChange={v => update('subcategory', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`svc-subcategory-${serviceGroup}-${idx}`}>
                <SelectValue placeholder="Select subcategory" />
              </SelectTrigger>
              <SelectContent>
                {selectedCat.subcategories.map(s => (
                  <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Provider / Responsible Staff</label>
            <Input value={svc.provider || ''} onChange={e => update('provider', e.target.value)}
              placeholder="e.g., Mrs. Johnson, SpEd Teacher" className="rounded-xl text-sm h-9"
              data-testid={`svc-provider-${serviceGroup}-${idx}`} />
          </div>
        )}
      </div>

      {/* Row 2: Provider (if subcategory shown above) + Delivery Type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {hasSubcategories && (
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Provider / Responsible Staff</label>
            <Input value={svc.provider || ''} onChange={e => update('provider', e.target.value)}
              placeholder="e.g., Mrs. Johnson, SpEd Teacher" className="rounded-xl text-sm h-9"
              data-testid={`svc-provider-${serviceGroup}-${idx}`} />
          </div>
        )}
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Service Type</label>
          <Select value={svc.delivery_type || undefined} onValueChange={v => update('delivery_type', v)}>
            <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`svc-delivery-${serviceGroup}-${idx}`}>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {profile.serviceDeliveryTypes.map(t => (
                <SelectItem key={t.id} value={t.label}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Location + Frequency + Duration */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Location</label>
          <Select value={svc.setting || undefined} onValueChange={v => update('setting', v)}>
            <SelectTrigger className="rounded-xl text-sm h-9" data-testid={`svc-location-${serviceGroup}-${idx}`}>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {profile.serviceLocations.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Frequency</label>
          <div className="flex gap-1.5">
            <Input value={svc.frequency_amount || ''} onChange={e => update('frequency_amount', e.target.value)}
              placeholder="3" className="rounded-xl text-sm h-9 w-16" type="number" min="0"
              data-testid={`svc-freq-amt-${serviceGroup}-${idx}`} />
            <span className="text-xs text-[#595959] self-center">x</span>
            <Select value={svc.frequency_unit || 'per week'} onValueChange={v => update('frequency_unit', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9 flex-1" data-testid={`svc-freq-unit-${serviceGroup}-${idx}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {profile.frequencyUnits.map(u => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Duration (min/session)</label>
          <Input value={svc.minutes_per_session || ''} onChange={e => update('minutes_per_session', e.target.value)}
            placeholder="30" className="rounded-xl text-sm h-9" type="number" min="0"
            data-testid={`svc-duration-${serviceGroup}-${idx}`} />
        </div>
      </div>

      {/* Row 4: Start Date + End Date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Start Date</label>
          <Input type="date" value={svc.start_date || ''} onChange={e => update('start_date', e.target.value)}
            className="rounded-xl text-sm h-9" data-testid={`svc-start-${serviceGroup}-${idx}`} />
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">End Date</label>
          <Input type="date" value={svc.end_date || ''} onChange={e => update('end_date', e.target.value)}
            className="rounded-xl text-sm h-9" data-testid={`svc-end-${serviceGroup}-${idx}`} />
        </div>
      </div>

      {/* Row 5: Description / Notes */}
      <div>
        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Description / Notes</label>
        <Textarea value={svc.description || ''} onChange={e => update('description', e.target.value)}
          placeholder="Additional details about this service..." rows={2} className="rounded-xl text-sm"
          data-testid={`svc-desc-${serviceGroup}-${idx}`} />
      </div>
    </div>
  );
}

export default function ServicesEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);

  const addService = (category) => {
    setContent(prev => ({
      ...prev,
      [category]: [...(prev[category] || []), { ...EMPTY_SERVICE, id: `svc_${Date.now()}` }],
    }));
  };

  const removeService = (category, idx) => {
    setContent(prev => ({ ...prev, [category]: prev[category].filter((_, i) => i !== idx) }));
  };

  const updateService = (category, idx, field, value) => {
    setContent(prev => ({
      ...prev,
      [category]: prev[category].map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const addItem = (key, template) => {
    setContent(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), { ...template, id: `item_${Date.now()}` }],
    }));
  };

  const removeItem = (key, idx) => {
    setContent(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  const updateItem = (key, idx, field, value) => {
    setContent(prev => ({
      ...prev,
      [key]: prev[key].map((item, i) => i === idx ? { ...item, [field]: value } : item),
    }));
  };

  const updateField = (field, value) => setContent(prev => ({ ...prev, [field]: value }));
  const updateNested = (parent, field, value) => {
    setContent(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: value } }));
  };

  const totalServices = (content.special_education?.length || 0) + (content.related_services?.length || 0);
  const esy = content.extended_school_year || {};
  const transport = content.transportation || {};

  return (
    <div className="max-w-4xl space-y-6" data-testid="services-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="services-title">Services & Environment</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{totalServices} service{totalServices !== 1 ? 's' : ''}</Badge>
            <Badge className="bg-[#F3F4F1] text-[#595959] text-xs">{content.supplementary_aids?.length || 0} aids</Badge>
            <Badge className="bg-[#F3F4F1] text-[#595959] text-[10px]">{profile.label} Profile</Badge>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2C5A8B] text-white rounded-xl shrink-0" data-testid="services-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Scaffold Notice */}
      <div className="bg-[#3B6A9B]/5 border border-[#3B6A9B]/15 rounded-xl p-3 flex items-start gap-2">
        <Sparkles className="h-4 w-4 text-[#3B6A9B] shrink-0 mt-0.5" />
        <p className="text-xs text-[#3B6A9B]">AI-powered service suggestions and LRE consistency checks will be available in a future update.</p>
      </div>

      {/* Specially Designed Instruction (SDI) */}
      <CollapsibleGroup title="Specially Designed Instruction (SDI)" icon={GraduationCap}
        count={content.special_education?.length || 0} testId="sped-services-group">
        <div className="space-y-3 mt-4">
          {(content.special_education || []).map((svc, idx) => (
            <ServiceRow key={svc.id || idx} service={svc} idx={idx}
              serviceGroup="special_education"
              categoryList={profile.sdiCategories}
              onUpdate={updateService} onRemove={removeService} />
          ))}
          <Button variant="outline" className="rounded-xl text-xs" onClick={() => addService('special_education')} data-testid="add-sped-service">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add SDI Service
          </Button>
        </div>
      </CollapsibleGroup>

      {/* Related Services */}
      <CollapsibleGroup title="Related Services" icon={Briefcase}
        count={content.related_services?.length || 0} testId="related-services-group">
        <div className="space-y-3 mt-4">
          {(content.related_services || []).map((svc, idx) => (
            <ServiceRow key={svc.id || idx} service={svc} idx={idx}
              serviceGroup="related_services"
              categoryList={profile.relatedServices}
              onUpdate={updateService} onRemove={removeService} />
          ))}
          <Button variant="outline" className="rounded-xl text-xs" onClick={() => addService('related_services')} data-testid="add-related-service">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Related Service
          </Button>
        </div>
      </CollapsibleGroup>

      {/* Supplementary Aids & Supports */}
      <CollapsibleGroup title="Supplementary Aids & Supports" icon={ShieldCheck}
        count={content.supplementary_aids?.length || 0} testId="aids-group">
        <div className="space-y-3 mt-4">
          {(content.supplementary_aids || []).map((aid, idx) => (
            <div key={aid.id || idx} className="bg-[#FDFCFB] border border-[#E5E5E5] rounded-xl p-4" data-testid={`aid-row-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-[#595959]">Aid {idx + 1}</span>
                <button onClick={() => removeItem('supplementary_aids', idx)} className="p-1 rounded-lg hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Aid / Support Name</label>
                  <Input value={aid.name || ''} onChange={e => updateItem('supplementary_aids', idx, 'name', e.target.value)}
                    placeholder="e.g., Visual schedule, noise-canceling headphones" className="rounded-xl text-sm h-9" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Setting / Location</label>
                  <Input value={aid.setting || ''} onChange={e => updateItem('supplementary_aids', idx, 'setting', e.target.value)}
                    placeholder="e.g., All settings, Gen Ed classroom" className="rounded-xl text-sm h-9" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Description</label>
                <Textarea value={aid.description || ''} onChange={e => updateItem('supplementary_aids', idx, 'description', e.target.value)}
                  placeholder="Describe the aid or support..." rows={2} className="rounded-xl text-sm" />
              </div>
            </div>
          ))}
          <Button variant="outline" className="rounded-xl text-xs" onClick={() => addItem('supplementary_aids', EMPTY_AID)} data-testid="add-aid">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Supplementary Aid
          </Button>
        </div>
      </CollapsibleGroup>

      {/* Program Modifications */}
      <CollapsibleGroup title="Program Modifications" icon={Settings2}
        count={content.program_modifications?.length || 0} testId="modifications-group" defaultOpen={false}>
        <div className="space-y-3 mt-4">
          {(content.program_modifications || []).map((mod, idx) => (
            <div key={mod.id || idx} className="bg-[#FDFCFB] border border-[#E5E5E5] rounded-xl p-4" data-testid={`mod-row-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-[#595959]">Modification {idx + 1}</span>
                <button onClick={() => removeItem('program_modifications', idx)} className="p-1 rounded-lg hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Modification</label>
                  <Input value={mod.name || ''} onChange={e => updateItem('program_modifications', idx, 'name', e.target.value)}
                    placeholder="e.g., Modified curriculum, Reduced assignments" className="rounded-xl text-sm h-9" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Description</label>
                  <Input value={mod.description || ''} onChange={e => updateItem('program_modifications', idx, 'description', e.target.value)}
                    placeholder="Details about this modification" className="rounded-xl text-sm h-9" />
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="rounded-xl text-xs" onClick={() => addItem('program_modifications', EMPTY_MOD)} data-testid="add-modification">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Program Modification
          </Button>
        </div>
      </CollapsibleGroup>

      {/* Placement & LRE */}
      <CollapsibleGroup title="Placement & Least Restrictive Environment" icon={MapPin}
        count={0} badge={content.placement ? <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full ml-1">Set</span> : null}
        testId="lre-group">
        <div className="space-y-5 mt-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Placement / Educational Setting</label>
            <Select value={content.placement || undefined} onValueChange={v => updateField('placement', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="placement-select"><SelectValue placeholder="Select placement" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general_education">General Education (80%+ in Gen Ed)</SelectItem>
                <SelectItem value="resource">Resource Room (40-79% in Gen Ed)</SelectItem>
                <SelectItem value="separate_class">Separate Classroom (&lt;40% in Gen Ed)</SelectItem>
                <SelectItem value="separate_school">Separate School</SelectItem>
                <SelectItem value="residential">Residential Facility</SelectItem>
                <SelectItem value="home_hospital">Home / Hospital</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">% Time in General Education</label>
              <Input value={content.placement_percentage_gen_ed || ''} onChange={e => updateField('placement_percentage_gen_ed', e.target.value)}
                placeholder="e.g., 80" type="number" min="0" max="100" className="rounded-xl text-sm h-9" data-testid="pct-gen-ed" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">% Time in Special Education</label>
              <Input value={content.placement_percentage_sped || ''} onChange={e => updateField('placement_percentage_sped', e.target.value)}
                placeholder="e.g., 20" type="number" min="0" max="100" className="rounded-xl text-sm h-9" data-testid="pct-sped" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">LRE Explanation</label>
            <p className="text-xs text-[#595959] mb-2">Explain the extent to which the student will participate with nondisabled peers in general education classes and extracurricular activities.</p>
            <Textarea value={content.lre_explanation || ''} onChange={e => updateField('lre_explanation', e.target.value)}
              placeholder="Describe the student's participation in general education..." rows={4}
              className="rounded-xl text-sm" data-testid="lre-explanation" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Justification for Removal from General Education</label>
            <p className="text-xs text-[#595959] mb-2">If the student is removed from general education for any portion of the day, explain why the student's disability prevents satisfactory participation even with supplementary aids and services.</p>
            <Textarea value={content.lre_removal_justification || ''} onChange={e => updateField('lre_removal_justification', e.target.value)}
              placeholder="Justification for time outside general education..." rows={3}
              className="rounded-xl text-sm" data-testid="lre-justification" />
          </div>
        </div>
      </CollapsibleGroup>

      {/* Extended School Year */}
      <CollapsibleGroup title="Extended School Year (ESY)" icon={Sun}
        count={0} badge={esy.eligible != null ? <span className={`text-[10px] px-2 py-0.5 rounded-full ml-1 ${esy.eligible ? 'text-green-600 bg-green-50' : 'text-[#595959] bg-[#F3F4F1]'}`}>{esy.eligible ? 'Eligible' : 'Not Eligible'}</span> : null}
        testId="esy-group" defaultOpen={false}>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">ESY Eligibility</label>
            <Select value={esy.eligible === true ? 'yes' : esy.eligible === false ? 'no' : undefined} onValueChange={v => updateNested('extended_school_year', 'eligible', v === 'yes')}>
              <SelectTrigger className="rounded-xl text-sm h-9 w-48" data-testid="esy-eligible"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Eligible</SelectItem>
                <SelectItem value="no">Not Eligible</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Justification</label>
            <Textarea value={esy.justification || ''} onChange={e => updateNested('extended_school_year', 'justification', e.target.value)}
              placeholder="Justification for ESY eligibility determination..." rows={3} className="rounded-xl text-sm" data-testid="esy-justification" />
          </div>
        </div>
      </CollapsibleGroup>

      {/* Transportation */}
      <CollapsibleGroup title="Transportation" icon={Bus}
        count={0} badge={transport.specialized != null ? <span className={`text-[10px] px-2 py-0.5 rounded-full ml-1 ${transport.specialized ? 'text-[#3B6A9B] bg-[#3B6A9B]/10' : 'text-[#595959] bg-[#F3F4F1]'}`}>{transport.specialized ? 'Specialized' : 'Regular'}</span> : null}
        testId="transport-group" defaultOpen={false}>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Specialized Transportation Required?</label>
            <Select value={transport.specialized === true ? 'yes' : transport.specialized === false ? 'no' : undefined} onValueChange={v => updateNested('transportation', 'specialized', v === 'yes')}>
              <SelectTrigger className="rounded-xl text-sm h-9 w-48" data-testid="transport-specialized"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes - Specialized</SelectItem>
                <SelectItem value="no">No - Regular Transportation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {transport.specialized && (
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1.5 block">Transportation Description</label>
              <Textarea value={transport.description || ''} onChange={e => updateNested('transportation', 'description', e.target.value)}
                placeholder="Describe specialized transportation needs..." rows={2} className="rounded-xl text-sm" data-testid="transport-description" />
            </div>
          )}
        </div>
      </CollapsibleGroup>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2C5A8B] text-white rounded-xl" data-testid="services-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Services & Environment'}
        </Button>
      </div>
    </div>
  );
}
